import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ApplyPage() {
  const [applicationType, setApplicationType] = useState<'business' | 'personal' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    try {
      const submitUrl = applicationType === 'business' 
        ? 'https://readdy.ai/api/form/d6d7k1uqd308q1a09tp0'
        : 'https://readdy.ai/api/form/d6d7k1uqd308q1a09tpg';

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!applicationType) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-20">
          <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <p className="text-xs uppercase tracking-wider text-gray-600 mb-4 font-semibold">
                GET STARTED
              </p>
              <h1 className="text-5xl md:text-6xl font-bold text-navy mb-6">
                Apply for Funding
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose your funding path to begin your pre-qualification
              </p>
            </div>
          </section>

          <section className="py-20">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <button
                  onClick={() => setApplicationType('business')}
                  className="bg-white border-2 border-gray-200 rounded-xl p-10 text-left hover:border-navy hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mb-6">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  
                  <h2 className="text-2xl font-semibold text-navy mb-4">
                    Business Funding
                  </h2>
                  
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    Apply for business credit cards, lines of credit, term loans, and business credit building services.
                  </p>

                  <div className="flex items-center text-navy font-medium">
                    Apply Now
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </button>

                <button
                  onClick={() => setApplicationType('personal')}
                  className="bg-white border-2 border-gray-200 rounded-xl p-10 text-left hover:border-navy hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mb-6">
                    <i className="ri-user-line text-2xl text-white"></i>
                  </div>
                  
                  <h2 className="text-2xl font-semibold text-navy mb-4">
                    Personal Funding
                  </h2>
                  
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    Apply for personal loans, credit cards, and personal credit optimization services.
                  </p>

                  <div className="flex items-center text-navy font-medium">
                    Apply Now
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/buy-tradelines')}
                  className="bg-white border-2 border-gray-200 rounded-xl p-10 text-left hover:border-navy hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mb-6">
                    <i className="ri-line-chart-line text-2xl text-white"></i>
                  </div>
                  
                  <h2 className="text-2xl font-semibold text-navy mb-4">
                    Buy Tradelines
                  </h2>
                  
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    Browse and purchase available seasoned tradelines to strengthen your credit profile and improve funding eligibility.
                  </p>

                  <div className="flex items-center text-navy font-medium">
                    View Tradelines
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <button
              onClick={() => setApplicationType(null)}
              className="flex items-center text-sm text-gray-600 hover:text-navy mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to selection
            </button>

            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">
                {applicationType === 'business' ? 'Business Funding' : 'Personal Funding'} Application
              </h1>
              <p className="text-lg text-gray-600">
                Complete the form below to begin your pre-qualification
              </p>
            </div>

            <form 
              onSubmit={handleSubmit}
              data-readdy-form
              id={applicationType === 'business' ? 'business-funding-form' : 'personal-funding-form'}
              className="bg-white rounded-xl shadow-lg p-8 md:p-10"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-navy mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-navy mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-navy mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-navy mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                  />
                </div>

                {applicationType === 'business' && (
                  <>
                    <div>
                      <label htmlFor="businessName" className="block text-sm font-medium text-navy mb-2">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        name="businessName"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-navy mb-2">
                        Business Type *
                      </label>
                      <select
                        id="businessType"
                        name="businessType"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                      >
                        <option value="">Select business type</option>
                        <option value="LLC">LLC</option>
                        <option value="Corporation">Corporation</option>
                        <option value="Sole Proprietorship">Sole Proprietorship</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-navy mb-2">
                        Years in Business *
                      </label>
                      <select
                        id="yearsInBusiness"
                        name="yearsInBusiness"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                      >
                        <option value="">Select years</option>
                        <option value="Less than 1 year">Less than 1 year</option>
                        <option value="1-2 years">1-2 years</option>
                        <option value="2-5 years">2-5 years</option>
                        <option value="5+ years">5+ years</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="annualRevenue" className="block text-sm font-medium text-navy mb-2">
                        Annual Revenue *
                      </label>
                      <select
                        id="annualRevenue"
                        name="annualRevenue"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                      >
                        <option value="">Select revenue range</option>
                        <option value="Less than $50k">Less than $50k</option>
                        <option value="$50k - $100k">$50k - $100k</option>
                        <option value="$100k - $250k">$100k - $250k</option>
                        <option value="$250k - $500k">$250k - $500k</option>
                        <option value="$500k - $1M">$500k - $1M</option>
                        <option value="$1M+">$1M+</option>
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="creditScore" className="block text-sm font-medium text-navy mb-2">
                    Estimated Credit Score *
                  </label>
                  <select
                    id="creditScore"
                    name="creditScore"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                  >
                    <option value="">Select credit score range</option>
                    <option value="Below 600">Below 600</option>
                    <option value="600-650">600-650</option>
                    <option value="650-700">650-700</option>
                    <option value="700-750">700-750</option>
                    <option value="750+">750+</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fundingAmount" className="block text-sm font-medium text-navy mb-2">
                    Desired Funding Amount *
                  </label>
                  <select
                    id="fundingAmount"
                    name="fundingAmount"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                  >
                    <option value="">Select amount</option>
                    <option value="$10k - $25k">$10k - $25k</option>
                    <option value="$25k - $50k">$25k - $50k</option>
                    <option value="$50k - $100k">$50k - $100k</option>
                    <option value="$100k - $250k">$100k - $250k</option>
                    <option value="$250k+">$250k+</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fundingPurpose" className="block text-sm font-medium text-navy mb-2">
                    Funding Purpose *
                  </label>
                  <textarea
                    id="fundingPurpose"
                    name="fundingPurpose"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all resize-none"
                    placeholder="Please describe how you plan to use the funding (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-navy mb-2">
                    When do you need funding? *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none transition-all"
                  >
                    <option value="">Select timeline</option>
                    <option value="Immediately">Immediately</option>
                    <option value="Within 30 days">Within 30 days</option>
                    <option value="1-3 months">1-3 months</option>
                    <option value="3-6 months">3-6 months</option>
                    <option value="Just exploring">Just exploring</option>
                  </select>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Application submitted successfully! We'll contact you within 24-48 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <i className="ri-error-warning-fill mr-2"></i>
                    There was an error submitting your application. Please try again.
                  </p>
                </div>
              )}

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-navy text-white py-4 rounded-lg font-medium hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-6">
                By submitting this form, you agree to our Terms of Service and Privacy Policy. We will never share your information without your consent.
              </p>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
